<script>
import { reactive } from 'vue'
import { mapMutations, mapActions } from 'vuex'
import * as def from '@/lib/definition'
import { datestr } from '@/lib/common'
import imageFileLoader from './image-file-loader'
import imageViewer from '@/components/image-viewer.vue'
import textEditor from '@/components/text-editor.vue'

export default {
  components: {
    imageFileLoader,
    imageViewer,
    textEditor
  },
  props: [
    'params'
  ],
  setup (props) {
    const paragraphs = (() => {
      if (props.params.paragraphs) {
        return props.params.paragraphs.map(p => ({ ...p }))
      } else {
        return []
      }
    })()
    const details = (() => {
      if (props.params.details) {
        return props.params.details.map(detail => ({ ...detail }))
      } else {
        return []
      }
    })()
    return reactive({
      def,
      values: reactive({ ...props.params }),
      images: props.params.images ? [...props.params.images] : [],
      tags: props.params.tags ? [...props.params.tags] : [],
      paragraphs,
      details,
      detailName: null,
      detailValue: null,
      tagName: null,
      showImageFileLoader: false,
      showThumbFileLoader: false,
      maxSize: def.maxSize
    })
  },
  computed: {
    path () {
      return this.$route.path
    },
    url () {
      const { pageId } = this.$route.params
      const articleId = this.values.id
      return `${def.host}/${pageId}/${articleId}`
    },
    publicationDate: {
      get () {
        return this.values.publication_date
          ? datestr(this.values.publication_date, datestr.format)
          : null
      },
      set (value) {
        this.values.publication_date = new Date(value)
      }
    },
    updateDate: {
      get () {
        return this.values.update_date
          ? datestr(this.values.update_date, datestr.format)
          : null
      },
      set (value) {
        this.values.update_date = new Date(value)
      }
    }
  },
  methods: {
    ...mapMutations([
      'enqueueAsync',
      'dequeueAsync',
      'setEditor'
    ]),
    ...mapActions([
      'fetchArticle',
      'updateArticle',
      'deleteArticle',
      'uploadStorage',
      'addImage'
    ]),
    addDetail () {
      this.details.push({
        name: this.detailName.trim(),
        content: ''
      })
      this.detailName = null
      this.detailValue = null
    },
    deleteDetail (index) {
      this.details.splice(index, 1)
    },
    swapDetail (index) {
      const [detail] = this.details.splice(index, 1)
      this.details.splice(index + 1, 0, detail)
    },
    addTag () {
      this.tags.push(this.tagName)
      this.tagName = null
    },
    deleteTag (index) {
      this.tags.splice(index, 1)
    },
    swapTag (index) {
      const [tag] = this.tags.splice(index, 1)
      this.tags.splice(index + 1, 0, tag)
    },
    update () {
      const { pageId } = this.$route.params
      const articleId = this.values.id
      const data = { ...this.values }
      if (this.params.images) {
        data.images = this.images
      }
      if (this.params.paragraphs) {
        data.paragraphs = this.paragraphs
      }
      if (this.params.details) {
        data.details = this.details
      }
      if (this.params.tags) {
        data.tags = this.tags
      }
      delete data.id
      this.enqueueAsync()
      this.updateArticle({
        pageId,
        articleId,
        data: data
      })
        .then(() => {
          this.fetchArticle({ pageId, articleId })
          this.setEditor(false)
          if (!this.params.id) {
            this.$router.push({
              name: 'article', params: { pageId, articleId }
            })
          }
        })
        .catch(error => alert(error))
        .finally(() => this.dequeueAsync())
    },
    create () {
      const { pageId } = this.$route.params
      const articleId = this.values.id.toLowerCase()
      this.values.id = articleId
      if (!this.values.publication_date) {
        const date = new Date()
        date.setHours(date.getHours() + 1)
        this.values.publication_date = date
        alert('公開時間が未入力のため1時間後に設定しました。')
      }
      this.fetchArticle({ pageId, articleId })
        .then(() => {
          alert(`${pageId}/${articleId}は既に登録されています。`)
        })
        .catch(() => {
          this.update()
        })
    },
    deleteDoc () {
      const { pageId, articleId } = this.$route.params
      if (!confirm(`${pageId}/${articleId}を削除します。よろしいですか？`)) {
        return false
      }
      this.deleteArticle({
        pageId,
        articleId
      })
        .then(() => {
          this.setEditor(false)
          this.$router.replace({
            name: 'page',
            params: { pageId: this.$route.params.pageId }
          })
        })
    },
    uploadImageFile (file, thumb) {
      const { pageId } = this.$route.params
      const filename = Math.random().toString(32).substring(2)
      const fullPath = `pages/${pageId}/${filename}.${file.ext}`
      this.enqueueAsync()
      this.uploadStorage({
        fullPath,
        source: file.blob,
        metadata: {
          cacheControl: 'public,max-age=3600',
          customMetadata: { fileName: file.name }
        }
      })
        .then(() => {
          this.showImageFileLoader = false
          this.showThumbFileLoader = false
          if (thumb) {
            this.values.thumbnail = fullPath
          } else {
            this.images.push(fullPath)
          }
          this.addImage({ pageId, filename, fullPath })
          console.log('Uploaded a file!')
        })
        .catch(error => console.error(error))
        .finally(() => this.dequeueAsync())
      return false
    },
    removeImage (index) {
      if (index === 'thumbnail') {
        this.values.thumbnail = ''
      } else {
        this.images.splice(index, 1)
      }
    },
    swapImage (index) {
      const [image] = this.images.splice(index, 1)
      this.images.splice(index + 1, 0, image)
    },
    trim (keys) {
      if (typeof this[keys[0]][keys[1]] === 'string') {
        this[keys[0]][keys[1]] = this[keys[0]][keys[1]].trim()
      } else if (typeof this[keys[0]][keys[1]] === 'object') {
        this[keys[0]][keys[1]].content = this[keys[0]][keys[1]].content.trim()
      }
    },
    updateParagraphs ({ index, value, replace = 1 }) {
      if (value.length) {
        this.paragraphs.splice(index, replace, ...value)
      } else {
        this.paragraphs.splice(index, replace)
      }
    }
  }
}
</script>

<template>
  <div class="editor level-center">
    <section @click.stop>
      <div class="editor-body">
      <header>
        <h1 v-if="params.id" class="is-size-1">記事を編集する</h1>
        <h1 v-else class="is-size-1">記事を作成する</h1>
      </header>
      <dl class="dl-column">
        <dt>URL</dt>
        <dd v-if="params.id">
          <a
            :href="url"
            target="_blank"
            class="level-left button is-plain"
          >
            <span>{{ url }}</span>
            <i class="material-icons">open_in_new</i>
          </a>
        </dd>
        <dd v-else>
          <span>{{ def.host }}/{{ $route.params.pageId }}/</span>
          <span>{{ params.id }}</span>
          <input
            type="text"
            v-model="values.id"
            @blur="trim(['values', 'id'])"
            class="width-6rem"
          >
        </dd>
        <dt>{{ def.itemNames['title'] }}</dt>
        <dd>
          <input
            type="text"
            v-model="values.title"
            @blur="trim(['values', 'title'])"
            class="width-30rem"
          >
        </dd>
        <dt>{{ def.itemNames['publication_date'] }}</dt>
        <dd>
          <input
            type="datetime-local"
            v-model="publicationDate"
            class="width-auto"
          >
        </dd>
        <!--<template v-if="params.description !== undefined">
          <dt>{{ def.itemNames['description'] }}</dt>
          <dd><textarea v-model="values.description" rows="3"></textarea></dd>
        </template>-->
        <template v-if="params.thumbnail !== undefined">
          <dt>{{ def.itemNames['thumbnail'] }}</dt>
          <dd class="thumbnail editor-image level-left">
            <image-viewer
              v-if="values.thumbnail"
              :key="values.thumbnail"
              :fullPath="values.thumbnail"
            >
              <button
                class="image-remove icon"
                @click.stop="removeImage('thumbnail')"
              >
                <i class="material-icons">remove_circle</i>
              </button>
            </image-viewer>
            <button
              @click.stop="showThumbFileLoader = true"
              class="add-image level button"
            >
              <i class="material-icons">add_circle</i>
              <span v-if="values.thumbnail">{{ def.itemNames['thumbnail'] }}を変更する</span>
              <span v-else>{{ def.itemNames['thumbnail'] }}を登録する</span>
            </button>
          </dd>
        </template>
        <template v-if="params.images !== undefined">
          <dt>{{ def.itemNames['images'] }}</dt>
          <dd class="editor-images image-list">
            <transition-group
              name="flip-list"
              tag="ul"
              class="level-left"
            >
              <image-viewer
                v-for="(image, index) in images"
                :key="image"
                :fullPath="image"
                tag="li"
                class="editor-image"
              >
                <button
                  class="image-remove icon"
                  @click.stop="removeImage(index)"
                >
                  <i class="material-icons">remove_circle</i>
                </button>
                <button
                  class="image-swap icon"
                  v-if="index < images.length - 1"
                  @click.stop="swapImage(index)"
                >
                  <i class="material-icons is-size-0">swap_horizontal_circle</i>
                </button>
              </image-viewer>
              <li key="add_circle">
                <button
                  @click.stop="showImageFileLoader = true"
                  class="add-image level button"
                >
                  <i class="material-icons">add_circle</i>
                  <span>{{ def.itemNames['images'] }}を追加する</span>
                </button>
              </li>
            </transition-group>
          </dd>
        </template>
        <template v-if="params.paragraphs !== undefined">
          <dt>{{ def.itemNames['text'] }}</dt>
          <dd>
            <text-editor
              :paragraphs="paragraphs"
              @commit="updateParagraphs($event)"
              mode="edit"
            />
          </dd>
        </template>
        <template v-if="params.text !== undefined">
          <dt>{{ def.itemNames['text'] }}</dt>
          <dd>
            <textarea
              v-model="values.text"
              rows="3"
              @blur="trim(['values', 'text'])"
            ></textarea>
          </dd>
        </template>
        <template v-if="params.details !== undefined">
          <dt>{{ def.itemNames['details'] }}</dt>
          <dd class="editor-details">
            <transition-group
              name="flip-list"
              tag="ul"
            >
              <li
                v-for="(detail, index) in details"
                :key="detail.name"
                class="level-left"
              >
                <span class="is-size-5 width-10rem">{{ detail.name }}: </span>
                <input
                  type="text"
                  v-model="detail.content"
                  @blur="trim(['details', index])"
                >
                <button
                  @click.stop="deleteDetail(index)"
                  class="icon"
                >
                  <i class="material-icons">remove_circle</i>
                </button>
                <button
                  class="detail-swap icon"
                  v-if="index < details.length - 1"
                  @click.stop="swapDetail(index)"
                >
                  <i class="material-icons">swap_vertical_circle</i>
                </button>
              </li>
              <li class="level-left add-detail">
                <input
                  type="text"
                  v-model="detailName"
                  placeholder="項目名を入力"
                  class="width-6rem"
                >
                <!--<input type="text" v-model="detailValue">-->
                <button
                  @click.stop="addDetail"
                  class="button"
                >
                  <i class="material-icons">add_circle</i>
                  <span>項目を追加する</span>
                </button>
              </li>
            </transition-group>
          </dd>
        </template>
        <template v-if="params.tags !== undefined">
          <dt>{{ def.itemNames['tags'] }}</dt>
          <dd class="editor-tags">
            <transition-group
              name="flip-list"
              tag="ul"
              class="tag-list level-left is-mobile"
            >
              <li
                v-for="(tag, index) in tags"
                :key="tag"
                class="level-left"
              >
                <span class="is-size-5">{{ tag }}</span>
                <button
                  @click.stop="deleteTag(index)"
                  class="icon"
                >
                  <i class="material-icons level is-mobile is-size-2">remove_circle</i>
                </button>
                <button
                  class="tag-swap icon"
                  v-if="index < tags.length - 1"
                  @click.stop="swapTag(index)"
                >
                  <i class="material-icons is-size-2">swap_horizontal_circle</i>
                </button>
              </li>
            </transition-group>
            <div class="level-left add-detail">
              <input
                type="text"
                v-model="tagName"
                placeholder="タグを入力"
                class="width-12rem"
              >
              <button
                @click.stop="addTag"
                class="button"
              >
                <i class="material-icons">add_circle</i>
                <span>タグを追加する</span>
              </button>
            </div>
          </dd>
        </template>
      </dl>
      </div>
      <nav class="level is-mobile">
        <div>
          <button
            v-if="params.id"
            @click.stop="deleteDoc"
            class="button"
          >
            <i class="material-icons">delete</i>
            <span>記事を削除</span>
          </button>
        </div>
        <div class="level">
          <button
            @click.stop="setEditor(false)"
            class="button"
          >
            <i class="material-icons">cancel</i>
            <span>保存せずに閉じる</span>
          </button>
          <button
            @click.stop="params.id ? update() : create()"
            :disabled="!values.id || !values.title"
            class="button"
          >
            <i class="material-icons">check_circle</i>
            <span>保存して閉じる</span>
          </button>
        </div>
      </nav>
    </section>
    <transition name="fade">
      <image-file-loader
        v-if="showImageFileLoader"
        :maxSize="maxSize"
        @load="uploadImageFile($event)"
        @cancel="showImageFileLoader = false"
      />
      <image-file-loader
        v-else-if="showThumbFileLoader"
        :maxSize="maxSize"
        @load="uploadImageFile($event, true)"
        @cancel="showThumbFileLoader = false"
      />
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@use "../styles/variables";
.editor {
  position: fixed;
  left: 0;
  top: 0;
  background-color: variables.$black-transparent;
  width: 100vw;
  height: 100vh;
  z-index: 1;
  section {
    background-color: variables.$gray-lighter;
    border: solid 1px grey;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow: hidden;
    position: relative;
    max-height: 90vh;
    width: calc(100% - 2rem);
    min-width: 40rem;
    max-width: 60rem;
    .editor-body {
      overflow: scroll;
      max-height: calc(90vh - 5rem);
      padding-bottom: 3rem;
      margin-bottom: 4rem;
    }
    nav {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 5rem;
      background-color: variables.$gray-lighter;
      border-top: solid 1px grey;
      padding: 1rem;
    }
  }
  .editor-images {
    ul {
      li.editor-image {
        margin: 1rem;
      }
      .add-image {
        width: 9rem;
      }
    }
  }
  .thumbnail {
    margin: 0 !important;
    > div {
      margin: 1rem;
    }
  }
  .editor-image {
    margin: 1rem;
    .image-remove {
      position: absolute;
      top: -.75rem;
      right: -.75rem;
    }
    .image-swap {
      position: absolute;
      top: calc(50% - 1rem);
      right: -2rem;
      z-index: 1;
    }
  }
  .editor-details {
    ul {
      margin: 0 1rem;
      li {
        margin: .875rem 0;
        position: relative;
        .detail-swap {
          position: absolute;
          left: 12rem;
          top: 1.375rem;
        }
      }
    }
  }
  .editor-tags {
    .tag-list {
      li {
        position: relative;
        margin-right: 1.25rem;
        button {
          margin-left: .25rem;
        }
        .tag-swap {
          position: absolute;
          right: -1.5rem;
          top: 0;
        }
      }
    }
  }
  button {
    margin-left: 1rem;
  }
  input {
    width: 20rem;
  }
  .add-detail {
    input {
      width: 12rem;
    }
  }
  textarea {
    width: 96%;
  }
}
</style>
